import {
	Checkbox,
	DefaultButton,
	Dialog,
	DialogFooter,
	IColumn,
	Label,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	ProgressIndicator,
	SelectionMode,
	ShimmeredDetailsList,
	Stack
} from "@fluentui/react"
import { useEffect, useState } from "react"
import { makeApiCall } from "../services/apiService"
import { IPlanSelectorPlan } from "../types/IGroupPlan"

interface IPlanSelectorProps {
	displayOnly: boolean
	onlyShowPlans?: string[]
	restrictByAccountId?: string
	onSave: (plans: IPlanSelectorPlan[]) => void
	onDismiss: () => void
	defaultSelectedPlans: string[]
	isCRM?: boolean
}

interface IPlanDetail {
	cw_name: string
	"cw_basedeductible@OData.Community.Display.V1.FormattedValue": string
	cw_brandprescriptioncost: string
	"cw_maxoutofpocket@OData.Community.Display.V1.FormattedValue": string
	"cw_plantype@OData.Community.Display.V1.FormattedValue": string
	cw_coinsurance: string
	cw_pcpvisit: string
	cw_urgentcare: string
	cw_specialistvisit: string
	cw_universalplansid: string
}

interface IPlanTableProps {
	loading: boolean
	plans: IPlanDetail[]
	columns: IColumn[]
}

export function PlanTable(props: IPlanTableProps): JSX.Element {
	const { loading, plans, columns } = props
	return (
		<div data-is-scrollable="true" style={{ maxHeight: 500, overflowY: "auto" }}>
			<ShimmeredDetailsList enableShimmer={loading} items={plans} columns={columns} selectionMode={SelectionMode.none} />
		</div>
	)
}

export function PlanSelector(props: IPlanSelectorProps): JSX.Element {
	const { displayOnly, onlyShowPlans } = props
	const [selection, setSelection] = useState<IPlanDetail[]>([])

	const [plans, setPlans] = useState<IPlanDetail[]>()
	const [loading, setLoading] = useState<boolean>(true)
	const [error, setError] = useState<string | undefined>(undefined)

	const getPlans = async () => {
		try {
			let api = `/cw_universalplanses?$select=*&$filter=cw_enabledforquoting eq true and statecode eq 0&$orderby=cw_plancode asc`
			if (props.restrictByAccountId) {
				api = `/cw_universalplanses?$select=*&$expand=cw_cw_groupplans_Plan_cw_universalplans($select=cw_name,cw_groupplansid;$filter=(_cw_group_value eq '${props.restrictByAccountId}'))&$filter=cw_enabledforquoting eq true and statecode eq 0 and (cw_cw_groupplans_Plan_cw_universalplans/any(o1:(o1/_cw_group_value eq ${props.restrictByAccountId})))&$orderby=cw_plancode asc`
			}
			let customAPIPath = ""

			if (props.isCRM) {
				customAPIPath = "api/data/v9.2.24042.218"
			}
			const response = await makeApiCall(api, "GET", undefined, customAPIPath, undefined, undefined, undefined, undefined, `https://${window.top.location.hostname}`)
			setPlans(response.value)
			setSelection(response.value.filter(p => props.defaultSelectedPlans.includes(p.cw_name)))
			setLoading(false)
		} catch (error) {
			setError(error)
			setLoading(false)
		}
	}

	useEffect(() => {
		getPlans()
	}, [])

	const columns: IColumn[] = [
		displayOnly === false && {
			key: "check",
			name: "",
			/*onRenderHeader: () => {
				return <Checkbox styles={{ root: { paddingTop: 10 } }} onChange={(_e, checked) => setSelection(checked ? plans : [])} checked={selection.length === plans?.length} />
			},*/
			isIconOnly: true,
			minWidth: 40,
			maxWidth: 40,
			onRender: (item: IPlanDetail) => {
				return (
					<Checkbox
						disabled={selection.length >= 6 && !selection.some(s => s.cw_name === item.cw_name)}
						checked={selection.some(s => s.cw_name === item.cw_name)}
						onChange={(_e, checked) => {
							if (checked) {
								if (selection.length <= 6) {
									setSelection([...selection, item])
								}
							} else {
								setSelection(selection.filter(s => s.cw_name !== item.cw_name))
							}
						}}
					/>
				)
			}
		},
		{ key: "column1", name: "Name", fieldName: "cw_name", minWidth: 80, maxWidth: 80, isResizable: true },
		{ key: "column2", name: "Deductible", fieldName: "cw_basedeductible@OData.Community.Display.V1.FormattedValue", minWidth: 90, maxWidth: 90, isResizable: true },
		{ key: "column3", name: "Max OOP", fieldName: "cw_maxoutofpocket@OData.Community.Display.V1.FormattedValue", minWidth: 90, maxWidth: 90, isResizable: true },
		{
			name: "Copay",
			key: "column4",
			minWidth: 260,
			maxWidth: 260,
			isResizable: true,
			onRender: (item: IPlanDetail) => {
				return (
					<div>
						<table cellPadding={4} cellSpacing={4} style={{ fontSize: 12 }}>
							<tr>
								<td style={{ paddingRight: 8 }}>PCP/Mental Health Office Visit</td>
								<td>${item.cw_pcpvisit}</td>
							</tr>
							<tr>
								<td style={{ paddingRight: 8 }}>Specialty Office Visit</td>
								<td>${item.cw_specialistvisit}</td>
							</tr>
							<tr>
								<td style={{ paddingRight: 8 }}>Urgent Care Visit</td>
								<td>${item.cw_urgentcare}</td>
							</tr>
						</table>
					</div>
				)
			}
		},
		{
			key: "column5",
			name: "Coinsurance",
			minWidth: 100,
			maxWidth: 200,
			isResizable: true,
			onRender: (item: IPlanDetail) => {
				return <>{item.cw_coinsurance || "N/A"}</>
			}
		}
	].filter(Boolean) as IColumn[]

	return displayOnly === false ? (
		<Dialog minWidth={860} hidden={false} onDismiss={() => props.onDismiss()} dialogContentProps={{ title: "Select Plans" }}>
			{loading && <ProgressIndicator />}
			{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
			<Stack tokens={{ childrenGap: 8 }}>
				<Stack horizontal grow tokens={{ childrenGap: 8 }}>
					<Stack.Item grow>
						<Label>{plans?.length > 0 && `Select up to 6 plans and click "Save"`}</Label>
					</Stack.Item>
					<PrimaryButton
						onClick={() => {
							props.onSave(selection.map(p => ({ id: p.cw_universalplansid, name: p.cw_name })))
						}}
						text="Save"
						disabled={selection.length === 0 || selection.length > 6}
					/>
					<Stack.Item align="end">
						<Label>{selection.length} selected</Label>
					</Stack.Item>
				</Stack>
				<PlanTable loading={loading} plans={plans || []} columns={columns} />
			</Stack>
			<DialogFooter>
				<PrimaryButton
					onClick={() => {
						props.onSave(selection.map(p => ({ id: p.cw_universalplansid, name: p.cw_name })))
					}}
					text="Save"
					disabled={selection.length === 0 || selection.length > 6}
				/>
				<DefaultButton onClick={props.onDismiss} text="Close" />
			</DialogFooter>
		</Dialog>
	) : !loading && !error && plans ? (
		<PlanTable loading={loading} plans={onlyShowPlans ? plans.filter(p => onlyShowPlans?.includes(p.cw_name)) : plans || []} columns={columns} />
	) : (
		<ProgressIndicator />
	)
}
